import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { EvidenceBadge } from "../components/Widgets";
import { Clock, FileText, TrendingUp, Zap, ChevronRight, Users, Mail, Trash2 } from "lucide-react";

export default function Dashboard() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [cabinet, setCabinet] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [acceptCode, setAcceptCode] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    Promise.all([api.get("/dashboard/stats"), api.get("/cases"), api.get("/cabinet/members").catch(() => ({ data: null }))]).then(([s, c, cab]) => {
      setStats(s.data);
      setCases(c.data);
      setCabinet(cab.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, navigate]);

  const invite = async () => {
    try {
      const r = await api.post("/cabinet/invite", { email: inviteEmail });
      setInviteMsg({ ok: true, text: `✓ Invitation créée. Code: ${r.data.invite_id}${r.data.email_sent ? " (email envoyé)" : " (email en échec, transmettez le code manuellement)"}` });
      setInviteEmail("");
      api.get("/cabinet/members").then((cab) => setCabinet(cab.data));
    } catch (e) {
      setInviteMsg({ ok: false, text: "Erreur : " + (e.response?.data?.detail || "réessayez") });
    }
  };

  const acceptInvite = async () => {
    try {
      await api.post("/cabinet/accept", { invite_id: acceptCode });
      setInviteMsg({ ok: true, text: "Vous avez rejoint le cabinet." });
      // Refresh auth context AND cabinet state
      const [meRes, cabRes, statsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/cabinet/members").catch(() => ({ data: null })),
        api.get("/dashboard/stats"),
      ]);
      setUser(meRes.data);
      setStats(statsRes.data);
      setCabinet(cabRes.data);
      setAcceptCode("");
    } catch (e) {
      setInviteMsg({ ok: false, text: "Erreur : " + (e.response?.data?.detail || "code invalide") });
    }
  };

  const eraseData = async () => {
    if (!window.confirm("⚠️ Suppression IRRÉVERSIBLE de votre compte et de TOUTES vos données. Confirmer ?")) return;
    try {
      const r = await api.delete("/users/me/data");
      alert(`✓ Données effacées : ${JSON.stringify(r.data.deleted)}`);
      window.location.href = "/";
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || "réessayez"));
    }
  };


  if (loading || !user || !stats) return <div className="min-h-screen flex items-center justify-center"><div className="pulse-dot"></div></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="j-label mb-2">Dashboard · {user.role === "lawyer" || user.role === "admin" ? "Avocat" : "Particulier"}</div>
            <h1 className="j-h1 font-heading">Bonjour, {user.name?.split(" ")[0] || "vous"}.</h1>
            <p className="text-slate-600 mt-2">Plan actuel : <span className="font-semibold text-[#1E293B]">{stats.plan}</span></p>
          </div>
          <Link to="/#intake" className="btn-emerald" data-testid="new-case-btn"><Zap className="w-4 h-4" /> Nouvelle Fiche Flash</Link>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={<FileText />} label="Fiches générées" value={stats.total_cases} testId="stat-cases" />
          <StatCard icon={<Clock />} label="Temps économisé" value={`${stats.time_saved_hours}h`} sub={`${stats.time_saved_minutes} min`} testId="stat-time" />
          <StatCard icon={<TrendingUp />} label="ROI par dossier" value="47 s" sub="Structuration factuelle" testId="stat-roi" />
          <div className="j-card bg-[#1E293B] text-white" data-testid="stat-upgrade">
            <div className="j-label text-slate-300 mb-1">Passer Pro</div>
            <div className="font-heading font-black text-2xl">49€/mois</div>
            <Link to="/checkout/pro_monthly" className="text-[#10B981] text-sm font-semibold mt-2 inline-flex items-center gap-1">S'abonner <ChevronRight className="w-3 h-3" /></Link>
          </div>
        </div>

        {/* CABINET SECTION */}
        <div className="j-card mb-6" data-testid="cabinet-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-black text-xl text-[#1E293B] flex items-center gap-2"><Users className="w-5 h-5" /> Cabinet · Sièges partagés</h2>
            <div className="j-label">{(cabinet?.members || []).length} / 5 membres</div>
          </div>
          {stats.plan === "cabinet" || user.role === "admin" || (cabinet && cabinet.members && cabinet.members.some((m) => m.user_id === user.user_id)) ? (
            <>
              {(stats.plan === "cabinet" || user.role === "admin") && (
                <div className="flex gap-2 mb-3">
                  <input type="email" className="j-input flex-1" placeholder="email.collaborateur@cabinet.fr" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="cabinet-invite-input" />
                  <button onClick={invite} className="btn-emerald" data-testid="cabinet-invite-btn"><Mail className="w-4 h-4" /> Inviter</button>
                </div>
              )}
              <div className="space-y-1">
                {(cabinet?.members || []).map((m) => (
                  <div key={m.user_id} className="flex justify-between items-center text-sm py-2 border-b border-slate-100 last:border-0">
                    <span>{m.name} <span className="text-slate-400">· {m.email}</span></span>
                    <span className="text-xs text-[#10B981]">actif</span>
                  </div>
                ))}
                {(cabinet?.invites || []).map((i) => (
                  <div key={i.invite_id} className="flex justify-between items-center text-sm py-2 border-b border-slate-100 last:border-0">
                    <span className="text-slate-500">{i.email}</span>
                    <span className="text-xs text-amber-600">en attente · code {i.invite_id}</span>
                  </div>
                ))}
                {(!cabinet?.members || cabinet.members.length === 0) && (!cabinet?.invites || cabinet.invites.length === 0) && (
                  <div className="text-xs text-slate-400 py-2">Aucun membre pour l'instant.</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-600 space-y-3">
              <p>Rejoindre un cabinet avec un code d'invitation reçu par email :</p>
              <div className="flex gap-2">
                <input className="j-input flex-1" placeholder="inv_xxxxxxxxxxxx" value={acceptCode} onChange={(e) => setAcceptCode(e.target.value)} data-testid="cabinet-accept-input" />
                <button onClick={acceptInvite} className="btn-navy" data-testid="cabinet-accept-btn">Rejoindre</button>
              </div>
              <p className="text-xs text-slate-400">Pour créer un Cabinet (5 sièges) : <a href="/checkout/cabinet_monthly" className="text-[#10B981] font-semibold">Passer au plan Cabinet 149€/mois</a></p>
            </div>
          )}
          {inviteMsg && <div className={`text-sm mt-3 font-semibold ${inviteMsg.ok ? "text-feedback-success" : "text-feedback-error"}`} data-testid="cabinet-msg">{inviteMsg.text}</div>}
        </div>

        {/* CASES LIST */}
        <div className="j-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-black text-xl text-[#1E293B]">Historique des dossiers</h2>
            <div className="j-label">{cases.length} au total</div>
          </div>
          {cases.length === 0 && (
            <div className="text-center py-10 text-slate-500">
              <div>Aucun dossier pour le moment.</div>
              <Link to="/" className="btn-emerald mt-4">Créer votre premier dossier</Link>
            </div>
          )}
          <div className="space-y-2">
            {cases.map((c) => (
              <Link key={c.case_id} to={`/case/${c.case_id}`} className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors" data-testid={`case-row-${c.case_id}`}>
                <div className="w-10 h-10 rounded-lg bg-[#1E293B]/5 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-[#1E293B]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1E293B] truncate">{c.case_type} · {c.mode === "pro" ? "Avocat" : "Particulier"}</div>
                  <div className="text-xs text-slate-500 truncate">{c.description}</div>
                </div>
                <EvidenceBadge level={c.evidence_score || "amber"} />
                <div className="text-xs text-slate-400 hidden md:block">{new Date(c.created_at).toLocaleDateString("fr-FR")}</div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </div>
        {/* RGPD Zero-Data Retention */}
        <div className="j-card mt-6 border-red-200 bg-red-50/50" data-testid="rgpd-erase-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0"><Trash2 className="w-5 h-5" /></div>
            <div className="flex-1">
              <h2 className="font-heading font-black text-lg text-red-900">Effacer mes données en 1 clic</h2>
              <p className="text-sm text-red-700 mt-1">Politique Zero-Data Retention conforme RGPD (Art. 17). Suppression IRRÉVERSIBLE de votre compte, dossiers, preuves, actions et transactions.</p>
              <button onClick={eraseData} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white font-semibold text-sm hover:bg-red-700" data-testid="rgpd-erase-btn">
                <Trash2 className="w-4 h-4" /> 🗑️ Effacer mes données
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function StatCard({ icon, label, value, sub, testId }) {
  return (
    <div className="j-card" data-testid={testId}>
      <div className="flex items-center gap-2 j-label mb-2">{icon} <span>{label}</span></div>
      <div className="font-heading font-black text-3xl text-[#1E293B]">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
